import { RoleName } from '@core/types';
import StudyReportItem from 'app/features/reports/view/types/StudyReportItem';
import toolbarModule from 'ohif/extensions/evodicom/toolbarModule';

export type ReportActionId =
  | 'edit'
  | 'delete'
  | 'print'
  | 'print-no-template'
  | 'download'
  | 'info'
  | 'share';

// The viewer toolbar's button for each action. 'print' / 'download' have no
// rule of their own: they show with the Imprimir group ('print').
const TOOLBAR_BUTTON: Record<Exclude<ReportActionId, 'delete'>, string> = {
  edit: 'edit-report',
  print: 'print',
  'print-no-template': 'print-without-template',
  download: 'print',
  info: 'info',
  share: 'share',
};

interface ToolbarButton {
  id: string;
  display?: (context: unknown) => boolean;
  buttons?: ToolbarButton[];
}

export interface ReportVisibilityContext {
  role?: RoleName;
  isAnon: boolean;
  isAdministrator: boolean;
  readOnly: boolean;
  report?: StudyReportItem;
}

/** A toolbar button (top-level ones first) and the group it is in, if any. */
const findButton = (definitions: ToolbarButton[], id: string) => {
  const topLevel = definitions.find((definition) => definition.id === id);
  if (topLevel) {
    return { button: topLevel };
  }
  const group = definitions.find((definition) => definition.buttons?.some((b) => b.id === id));
  return { button: group?.buttons?.find((b) => b.id === id), group };
};

/**
 * The report actions the PDF report viewer offers: those the viewer toolbar
 * shows on the report's DOC viewport, evaluated with the toolbar's own rules
 * (ohif/extensions/evodicom/toolbarModule.js) on that viewport. Delete follows
 * the series thumbnail's rule (administrator or author).
 */
export const getVisibleReportActions = ({
  role,
  isAnon,
  isAdministrator,
  readOnly,
  report,
}: ReportVisibilityContext): ReportActionId[] => {
  if (!report) {
    return [];
  }

  const sop = report.sopInstanceUid;
  // Blob reports are not a DICOM series: stand in one so the rules' "series
  // of the study" check holds.
  const series = report.seriesInstanceUid || sop;
  const { definitions } = toolbarModule(
    report.generated && !readOnly ? [sop] : [],
    report.createdByCurrentUser ? [sop] : [],
    false,
  ) as { definitions: ToolbarButton[] };
  const context = {
    role,
    isAnon,
    studies: [{ series: [{ SeriesInstanceUID: series }] }],
    activeViewport: { Modality: 'DOC', SeriesInstanceUID: series, SOPInstanceUID: sop },
  };
  const isShown = (button?: ToolbarButton) => !!button && (button.display?.(context) ?? true);

  const visible = (Object.keys(TOOLBAR_BUTTON) as (keyof typeof TOOLBAR_BUTTON)[]).filter((id) => {
    const { button, group } = findButton(definitions, TOOLBAR_BUTTON[id]);
    // A group's rule also gates its buttons.
    return (!group || isShown(group)) && isShown(button);
  });

  const canDelete = !readOnly && !isAnon && (isAdministrator || report.createdByCurrentUser);

  return canDelete ? [...visible, 'delete'] : visible;
};
